import { TransriptRequestFormState } from '../components/containers/RequestTranscriptContainer';
import { mnohsFetch } from '../util/MNOHSFetch';
import { logPrintableResult } from '../util/PrintableResultLogger';
import { Result, ResultPromise } from '../util/Result';
import { API } from './API';
import { dummyUser } from './mocks/user.mock';
import { User } from './models/User';

/** Implementation of the `API` interface using the MNOHS backend API */
export const mnohsAPIClient = (options: { baseURL: string }): API => {
  return {
    login: (idToken: string): ResultPromise<User, undefined> => {
      return new Promise<Result<User, undefined>>(resolve =>
        fetch(options.baseURL + '/authorize', {
          method: 'GET',
          credentials: 'include',
          headers: {
            Authorization: 'Bearer ' + idToken,
            'Content-Type': 'application/json',
          },
        })
          .then(responseJson => {
            const json = responseJson.json();
            return json;
          })
          .then(json => {
            resolve(Result.success(json));
          })
          .catch(error => console.error(error))
      );
    },
    editPronoun: (pronoun: string): ResultPromise<User, undefined> => Promise.resolve(Result.success(dummyUser)),
    editProfilePhoto: (url: string): ResultPromise<User, undefined> => Promise.resolve(Result.success(dummyUser)),
    requestTranscript: (formState: TransriptRequestFormState): ResultPromise<undefined, undefined> => {
      const data = { ...formState, applicationDeadline: new Date(formState.applicationDeadline) };
      return Promise.resolve(Result.success(undefined));
    },

    // New endpoints
    /** GET /v1/Students */
    getStudents: async token => {
      const response = await mnohsFetch(options.baseURL, {
        method: 'GET',
        url: `/v1/Students`,
        accessToken: token,
      });
      const students = await response.json();
      logPrintableResult(students);
      return students;
    },
    /** GET /v1/Students/{personId} */
    getStudentById: async (personId, token) => {
      const response = await mnohsFetch(options.baseURL, {
        method: 'GET',
        url: `/v1/Students/${personId}`,
        accessToken: token,
      });
      const student = await response.json();
      logPrintableResult(student);
      return student;
    },
    /** GET /v1/Students/search/{term} */
    filterStudents: async (term, token) => {
      const response = await mnohsFetch(options.baseURL, {
        method: 'GET',
        url: `/v1/Students/search/${term}`,
        accessToken: token,
      });
      const students = await response.json();
      logPrintableResult(students);
      return students;
    },
    /** PATCH /v1/Students/{personId} */
    updateStudent: async (personId, body, token) => {
      const response = await mnohsFetch(options.baseURL, {
        method: 'PATCH',
        url: `/v1/Students/${personId}`,
        accessToken: token,
        type: 'JSON',
        body,
      });
      const student = await response.json();
      logPrintableResult(student);
      return student;
    },
    /** POST /v1/Students - bulk upload students (csv upload) */
    bulkUploadStudents: async (csvFile, token) => {
      const formData = new FormData();
      formData.append('file', csvFile, csvFile.name);

      const response = await mnohsFetch(options.baseURL, {
        method: 'POST',
        url: `/v1/Students`,
        accessToken: token,
        type: 'FORM_DATA',
        body: formData,
      });

      const bulkUploadResult = await response.json();
      logPrintableResult(bulkUploadResult);
      return bulkUploadResult;
    },
    /** POST /v1/data-import - bulk upload student results (multiple csv upload) */
    bulkUploadStudentResults: async (csvFiles, token) => {
      const formData = new FormData();
      csvFiles.forEach(file => {
        formData.append('files', file, file.name);
      });

      const response = await mnohsFetch(options.baseURL, {
        method: 'POST',
        url: `/v1/data-import`,
        accessToken: token,
        type: 'FORM_DATA',
        body: formData,
      });

      const result = await response.json();
      logPrintableResult(result);
      return result;
    },
    /** GET /v1/Students/{personId}/courses */
    getStudentCourses: async (personId, token) => {
      const response = await mnohsFetch(options.baseURL, {
        method: 'GET',
        url: `/v1/Students/${personId}/courses`,
        accessToken: token,
      });
      const courses = await response.json();
      logPrintableResult(courses);
      return courses;
    },
    /** GET /v1/Students/{personId}/academic-check */
    getStudentCourseGroups: async (personId, token, abortController) => {
      const response = await mnohsFetch(options.baseURL, {
        method: 'GET',
        url: `/v1/Students/${personId}/academic-check`,
        accessToken: token,
        abortController,
      });
      const courseGroups = await response.json();
      logPrintableResult(courseGroups);
      return courseGroups;
    },
    /** POST /v1/Students/{personId}/transcript-request */
    studentRequestTranscript: async (personId, body, token) => {
      const response = await mnohsFetch(options.baseURL, {
        method: 'POST',
        url: `/v1/Students/${personId}/transcript-request`,
        accessToken: token,
        type: 'JSON',
        body,
      });
      const transcript = await response.text();
      logPrintableResult(transcript);
    },
    /** GET /v1/Students/{personId}/notifications */
    getStudentNotifications: async (personId, token) => {
      const response = await mnohsFetch(options.baseURL, {
        method: 'GET',
        url: `/v1/Students/${personId}/notifications`,
        accessToken: token,
      });
      const notifications = await response.json();
      logPrintableResult(notifications);
      return notifications;
    },
    /** GET /v1/Courses */
    getCourses: async token => {
      const response = await mnohsFetch(options.baseURL, {
        method: 'GET',
        url: `/v1/Courses`,
        accessToken: token,
      });
      const courses = await response.json();
      logPrintableResult(courses);
      return courses;
    },
    /** GET /v1/Courses/{courseId} */
    getCourseById: async (courseId, token) => {
      const response = await mnohsFetch(options.baseURL, {
        method: 'GET',
        url: `/v1/Courses/${courseId}`,
        accessToken: token,
      });
      const course = await response.json();
      logPrintableResult(course);
      return course;
    },
    /** GET /v1/TranscriptRequests */
    getTranscriptRequests: async token => {
      const response = await mnohsFetch(options.baseURL, {
        method: 'GET',
        url: `/v1/TranscriptRequests`,
        accessToken: token,
      });
      const list = await response.json();
      logPrintableResult(list);
      return list;
    },
    /** GET /v1/TranscriptRequests/paged */
    getTranscriptRequestsByPage: async (page, pageSize, token) => {
      const response = await mnohsFetch(options.baseURL, {
        method: 'GET',
        url: `/v1/TranscriptRequests/paged`,
        accessToken: token,
        query: { page, pageSize },
      });
      const list = await response.json();
      logPrintableResult(list);
      return list;
    },
    /** GET /v1/Notifications */
    getNotifications: async token => {
      const response = await mnohsFetch(options.baseURL, {
        method: 'GET',
        url: `/v1/Notifications`,
        accessToken: token,
      });
      const list = await response.json();
      logPrintableResult(list);
      return list;
    },
    /** GET /v1/Notifications/paged */
    getNotificationsByPage: async (page, pageSize, token) => {
      const response = await mnohsFetch(options.baseURL, {
        method: 'GET',
        url: `/v1/Notifications/paged`,
        accessToken: token,
        query: { page, pageSize },
      });
      const list = await response.json();
      logPrintableResult(list);
      return list;
    },
  };
};
